import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { FilterType } from 'src/app/components/lib/wizard/filters';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { useDiscount } from 'src/app/queries/discounts/discount';
import { Features } from 'src/app/state/types/loc-profile';
import { useFeatureCheck } from 'src/app/state/user-dispensaries';

import { CalculationMethods, DiscountAppliesTo, RewardApplyTo, TotalItemConditionType } from '../../models';
import { percentCalculationMethods } from '../shared/helpers';
import { ThresholdType, ItemGroupType, ApplicationMethodTypeId } from '../types/index';

import { ConfigureStepActions, configureStepDefaultState, configureStepReducer } from './configure';
import { DetailsStepActions, detailsStepDefaultState, detailsStepReducer } from './details';
import { OnlineDetailsStepActions, onlineDetailsStepDefaultState, onlineDetailsStepReducer } from './online-details';
import {
  defaultFilters,
  RequirementsStepActions,
  requirementsStepDefaultState,
  requirementsStepReducer,
} from './requirements';
import { RewardsStepActions, rewardsStepDefaultState, rewardsStepReducer } from './rewards';
import {
  applyInitialConfigureStep,
  applyInitialDetailsStep,
  applyInitialOnlineDetailsStep,
  applyInitialRequirementsStep,
  applyInitialRewardsStep,
  useDispatchWithSideEffect,
} from './utils/apply-initial-state';
import { validateInitialState } from './utils/validation';

import type { Filters, Requirement } from './requirements';
import type { Discount } from '../../models';
import type { InitialWizardState } from '../../types';
import type { FilterResourceType } from '../shared/filter';
import type { Constraint, Reward, Restrictions } from '../types';

type ThresholdDetails = {
  maximumItems?: number;
  minimumGrams?: number;
  minimumItems?: number;
  minimumSpend?: number;
  totalItemCondition?: TotalItemConditionType;
};

type EligibleProductsModalRequirement = number | 'reward';

export function useDiscountWizardState(initialDiscountId?: number, initialState?: InitialWizardState) {
  const [dirty, setDirtyState] = useState(false);
  const [discountId, setDiscountId] = useState(0);
  const [showEligibleProductsModal, setShowEligibleProductsModal] = useState(false);
  const [eligibleProductsModalRequirement, setEligibleProductsModalRequirement] =
    useState<EligibleProductsModalRequirement>('reward');
  const initialStateAppliedRef = useRef(false);
  const defaultStepToOpenOn = initialState?.defaultStepToOpenOn;
  const [configureStepState, configureStepDispatchBase] = useReducer(configureStepReducer, configureStepDefaultState);
  const [detailsStepState, detailsStepDispatchBase] = useReducer(detailsStepReducer, detailsStepDefaultState);
  const [requirementsStepState, requirementsStepDispatchBase] = useReducer(
    requirementsStepReducer,
    requirementsStepDefaultState
  );
  const [rewardsStepState, rewardsStepDispatchBase] = useReducer(rewardsStepReducer, rewardsStepDefaultState);
  const [onlineDetailsStepState, onlineDetailsStepDispatchBase] = useReducer(
    onlineDetailsStepReducer,
    onlineDetailsStepDefaultState
  );

  const useDiscountsLoyaltyMultiplierFix = useLDFlag(LD.DISCOUNTS_LOYALTY_MULTIPLIER_FIX, false);

  const reset = () => {
    configureStepDispatchBase({ type: ConfigureStepActions.RESET });
    detailsStepDispatchBase({ type: DetailsStepActions.RESET });
    requirementsStepDispatchBase({ type: RequirementsStepActions.RESET });
    rewardsStepDispatchBase({ type: RewardsStepActions.RESET });
    onlineDetailsStepDispatchBase({ type: OnlineDetailsStepActions.RESET });
    setDirtyState(false);
    setDiscountId(0);
    initialStateAppliedRef.current = false;
  };

  // Global wizard: ONLY use the explicitly provided ID
  useEffect(() => {
    if (initialDiscountId !== undefined) {
      // Programmatically opened wizard - use the provided discount ID
      setDiscountId(initialDiscountId);
    }
  }, [initialDiscountId]);

  const { data: discount, isLoading } = useDiscount(discountId);

  useEffect(() => {
    if (discount !== undefined && discount !== null) {
      initialize(discount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount, initialDiscountId]);

  // Reset the ref when discountId changes to allow initial state to be applied again
  useEffect(() => {
    initialStateAppliedRef.current = false;
  }, [discountId]);

  // Apply initial state for new discounts
  useEffect(() => {
    if (!initialState || discountId !== 0 || initialStateAppliedRef.current) {
      return;
    }

    // Validate initial state
    const validation = validateInitialState(initialState);
    if (!validation.valid) {
      console.error('Invalid initial wizard state:', validation.errors);
      // Continue anyway but log errors - we don't want to block the wizard from opening
    }

    initialStateAppliedRef.current = true;

    // Apply each step using helper functions
    applyInitialConfigureStep(initialState.configureStep, configureStepDispatchBase);
    applyInitialDetailsStep(initialState.detailsStep, detailsStepDispatchBase);
    applyInitialRequirementsStep(initialState.requirementsStep, requirementsStepDispatchBase);
    applyInitialRewardsStep(initialState.rewardsStep, rewardsStepDispatchBase);
    applyInitialOnlineDetailsStep(initialState.onlineDetailsStep, onlineDetailsStepDispatchBase);
  }, [initialState, discountId]);

  const loading = useMemo(() => discountId !== 0 && isLoading, [discountId, isLoading]);

  const externalLoyaltyEnabled = useFeatureCheck(Features.ShowExternalLoyalty);

  const getApplicationMethodId = (
    applicationMethodReq: number,
    loyaltyPointsToRedeemReq: number | null
  ): ApplicationMethodTypeId => {
    const applicationMethodIsManual = applicationMethodReq === 2;
    const applicationMethodIsByCode = applicationMethodReq === 3;
    const hasLoyaltyPointsToRedeem = loyaltyPointsToRedeemReq !== null;

    if (applicationMethodIsManual && hasLoyaltyPointsToRedeem && externalLoyaltyEnabled) {
      return ApplicationMethodTypeId.ExternalLoyalty;
    }

    if (applicationMethodIsByCode && hasLoyaltyPointsToRedeem) {
      return ApplicationMethodTypeId.DutchieLoyalty;
    }

    return applicationMethodReq;
  };

  // TODO: Break up this initialize method to have separate utility methods for each step that formats the initial state data correctly to make this more readable https://dutchie.atlassian.net/browse/POS-5277
  const initialize = (discount: Discount) => {
    configureStepDispatchBase({
      type: ConfigureStepActions.INITIALIZE,
      payload: {
        applicationMethod: getApplicationMethodId(discount.ApplicationMethodId, discount.LoyaltyPointValue),
        discountName: discount.DiscountDescription,
        discountCode: discount.DiscountCode ?? '',
        externalId: discount.ExternalId,
        loyaltyPointsToRedeem: discount.LoyaltyPointValue ?? '',
      },
    });

    const daysOfWeek = {
      Sun: discount.Sunday ?? false,
      Mon: discount.Monday ?? false,
      Tue: discount.Tuesday ?? false,
      Wed: discount.Wednesday ?? false,
      Thu: discount.Thursday ?? false,
      Fri: discount.Friday ?? false,
      Sat: discount.Saturday ?? false,
    };

    detailsStepDispatchBase({
      type: DetailsStepActions.INITIALIZE,
      payload: {
        activeDaysOfTheWeek: daysOfWeek,
        availableLocIds: discount.LocationRestrictions,
        availableOnline: discount.IsAvailableOnline,
        customerTypeIds: discount.Reward.Restrictions.CustomerType?.RestrictionIds ?? [],
        discountGroupIds: discount.RestrictToGroupIds,
        endDate: discount.ValidDateTo === '04/19/4200 12:00 AM' ? null : new Date(discount.ValidDateTo),
        endTime: discount.EndTime ?? '',
        firstTimeCustomerOption: discount.FirstTimeCustomerOnly,
        limitPerCustomer: Boolean(discount.MaxRedemptions),
        limitPerCustomerQuantity: discount.MaxRedemptions ? Number(discount.MaxRedemptions) : '',
        platformTypeRestrictions: discount.PlatformTypeRestrictions?.length ? discount.PlatformTypeRestrictions : [],
        paymentRestrictions: {
          PayByBankSignupIncentive: discount.PaymentRestrictions?.PayByBankSignupIncentive ?? false,
        },
        redemptionLimit: discount.RedemptionLimit ? Number(discount.RedemptionLimit) : '',
        requireManagerApproval: discount.RequireManagerApproval,
        restrictByDayOfWeek: Object.values(daysOfWeek)?.includes(true),
        segmentIds: discount.RestrictToSegmentIds,
        specifyEndDate: Boolean(discount.ValidDateTo && discount.ValidDateTo !== '04/19/4200 12:00 AM'),
        startDate: new Date(discount.ValidDateFrom),
        startTime: discount.StartTime ?? '',
        visited: false,
      },
    });

    const { ThresholdMax, ThresholdMin, ThresholdTypeId } = discount.Reward;
    const discountRewardThresholdDetails = getThresholdDetails({
      ThresholdMax,
      ThresholdMin,
      ThresholdTypeId,
    });
    const discountRewardFilters = getRewardFilters(discount.Reward.Restrictions);
    const conditionHasApplyTo = discountRewardThresholdDetails.totalItemCondition === TotalItemConditionType.Exactly;
    const inferredDiscountAppliesTo = inferDiscountAppliesTo(
      discount.Reward,
      discount.IsBundledDiscount,
      discount.Constraints.length
    );
    const inferredRewardApplyTo = inferRewardApplyTo(
      discount.Reward,
      discount.IsBundledDiscount,
      discount.Constraints.length,
      conditionHasApplyTo
    );

    const requirementsFromConstraints = mapConstraintsToRequirements(
      discount.Constraints,
      discount.Reward,
      discountRewardThresholdDetails,
      discountRewardFilters
    );

    requirementsStepDispatchBase({
      type: RequirementsStepActions.INITIALIZE,
      payload: {
        activeFilter: {
          requirement: undefined,
          filter: undefined,
        },
        visited: false,
        ...requirementsFromConstraints,
      },
    });

    const discountRewardIsPercent = percentCalculationMethods.includes(discount.Reward.CalculationMethodId);

    rewardsStepDispatchBase({
      type: RewardsStepActions.INITIALIZE,
      payload: {
        activeFilter: undefined,
        allowStacking: discount.CanStackAutomatically,
        amountOff: !discountRewardIsPercent ? discount.Reward.DiscountValue : '',
        condition: {
          applyTo: inferredRewardApplyTo,
          condition: discount.Reward.ThresholdTypeId ?? 0,
          filters: { ...discountRewardFilters }, // still need to map resource names based on the resource ids
          maximumItems: discountRewardThresholdDetails.maximumItems ?? '',
          minimumGrams: discountRewardThresholdDetails.minimumGrams ?? '',
          minimumItems: discountRewardThresholdDetails.minimumItems ?? '',
          minimumSpend: discountRewardThresholdDetails.minimumSpend ?? '',
          requireSameProduct: discount.Reward.ItemGroupTypeId === ItemGroupType.BySKU,
          allowNonCannabisItems: discount.Reward.IncludeNonCannabis,
          totalItemCondition: discountRewardThresholdDetails.totalItemCondition ?? 0,
          id: discount.Reward.DiscountRewardId || null,
        },
        discountAppliesTo: inferredDiscountAppliesTo,
        discountMethod: discount.Reward.CalculationMethodId,
        ignoreNetTaxes: discount.IgnoreNetTax,
        loyaltyMultiplier:
          useDiscountsLoyaltyMultiplierFix &&
          discount.Reward.CalculationMethodId === CalculationMethods.LoyaltyMultiplier
            ? discount.Reward.DiscountValue
            : '',
        manualApplyToMethod: discount.Reward.ManualDefaultApplyTo,
        percentOff: discountRewardIsPercent ? discount.Reward.DiscountValue : '',
        visited: false,
      },
    });

    onlineDetailsStepDispatchBase({
      type: OnlineDetailsStepActions.INITIALIZE,
      payload: {
        description: discount.DiscountMenuDisplayDetails?.MenuDisplayDescription ?? '',
        imageUrl: discount.DiscountMenuDisplayDetails?.MenuDisplayImageUrl ?? undefined,
        onlineName: discount.DiscountMenuDisplayDetails?.MenuDisplayName ?? discount.OnlineName ?? '',
        rank: discount.MenuDisplayRank ?? '',
        visited: false,
        displayId: discount.DiscountMenuDisplayDetails?.DiscountMenuDisplayId,
      },
    });
  };

  const inferDiscountAppliesTo = (
    reward: Omit<Reward, 'Products'>,
    isBundledDiscount: boolean,
    numberOfConstraints: number
  ) => {
    const { ApplyToOnlyOneItem, HighestOrLowest } = reward;
    // if IsBundledDiscount is true, then same products
    if (isBundledDiscount) {
      return DiscountAppliesTo.SameProducts;
    }

    // if IsBundledDiscount is false and constraints length is 0, then check ApplyToOnlyOneItem - if that is false, then it's same and if its true than we determine from HighestOrLowest
    if (!isBundledDiscount && numberOfConstraints === 0) {
      return evaluateDiscountAppliesToFromApplyToOnlyOneItem(ApplyToOnlyOneItem, HighestOrLowest);
    }

    return DiscountAppliesTo.DifferentProducts;
  };

  const evaluateDiscountAppliesToFromApplyToOnlyOneItem = (
    isApplyToOnlyOneItem: boolean,
    highestOrLowest: 'high' | 'low'
  ): DiscountAppliesTo => {
    if (isApplyToOnlyOneItem) {
      if (highestOrLowest === 'high') {
        return DiscountAppliesTo.HighestPricedItem;
      }
      return DiscountAppliesTo.LowestPricedItem;
    }
    return DiscountAppliesTo.SameProducts;
  };

  const inferRewardApplyTo = (
    reward: Omit<Reward, 'Products'>,
    isBundledDiscount: boolean,
    numberOfConstraints: number,
    conditionHasApplyTo: boolean
  ): RewardApplyTo => {
    const { ApplyToOnlyOneItem, HighestOrLowest } = reward;
    // if IsBundledDiscount is false and constraints length is greater than 0 and totalItemCondition is Exactly, then ApplyToOnlyOneItem will be condition.applyTo (if true the its highest or lowest, if its false then its all)
    if (!isBundledDiscount && numberOfConstraints > 0 && conditionHasApplyTo) {
      return evaluateRewardApplyToFromApplyToOnlyOneItem(ApplyToOnlyOneItem, HighestOrLowest);
    }
    return RewardApplyTo.all;
  };

  const evaluateRewardApplyToFromApplyToOnlyOneItem = (
    isApplyToOnlyOneItem: boolean,
    highestOrLowest: 'high' | 'low'
  ): RewardApplyTo => {
    if (isApplyToOnlyOneItem) {
      if (highestOrLowest === 'high') {
        return RewardApplyTo.high;
      }
      return RewardApplyTo.low;
    }
    return RewardApplyTo.all;
  };

  const filterNameByRestrictionsObjectKey: Record<keyof Omit<Restrictions, 'CustomerType'>, FilterResourceType> = {
    Brand: 'brand',
    Category: 'category',
    InventoryTag: 'inventory tag',
    Product: 'product',
    Strain: 'strain',
    Tag: 'product tag',
    Tier: 'pricing tier',
    Vendor: 'vendor',
    Weight: 'weight',
  };

  const getRewardFilters = (discountRewardRestrictions: Restrictions): Filters => {
    const restrictionFilters: Filters = { ...defaultFilters };

    Object.entries(discountRewardRestrictions).forEach(([restrictionKey, restrictionValue]) => {
      if (restrictionKey === 'CustomerType') {
        return;
      }
      const filterSelectionType = getFilterSelectionType({
        hasFilters: restrictionValue.RestrictionIds.length > 0,
        isExclusive: restrictionValue.IsExclusion,
      });
      const key: FilterResourceType = filterNameByRestrictionsObjectKey[restrictionKey as keyof Restrictions];
      const resourcesFromIds = restrictionValue.RestrictionIds.map((resourceId) => ({ resourceId, resourceName: '' })); // will have to fetch resource names based on key and ids from discount restrictions

      restrictionFilters[key] = { selectionType: filterSelectionType, resources: resourcesFromIds };
    });

    return restrictionFilters;
  };

  const getFilterSelectionType = ({ hasFilters, isExclusive }): FilterType => {
    if (!hasFilters) {
      return FilterType.NONE;
    }
    if (isExclusive) {
      return FilterType.EXCLUSIONARY;
    }
    return FilterType.INCLUSIONARY;
  };

  const getThresholdDetails = ({ ThresholdTypeId, ThresholdMax, ThresholdMin }): ThresholdDetails => {
    if (ThresholdTypeId === ThresholdType.ProductWeight) {
      return {
        maximumItems: undefined,
        minimumGrams: ThresholdMin,
        minimumItems: undefined,
        minimumSpend: undefined,
      };
    }

    if (ThresholdTypeId === ThresholdType.SpendingAmount) {
      return {
        maximumItems: undefined,
        minimumGrams: undefined,
        minimumItems: undefined,
        minimumSpend: ThresholdMin,
      };
    }

    if (ThresholdTypeId === ThresholdType.NumberOfItems) {
      let totalItemCondition = TotalItemConditionType.AtLeast;

      if (ThresholdMax && ThresholdMin && ThresholdMax > ThresholdMin) {
        totalItemCondition = TotalItemConditionType.Between;
      }

      if (ThresholdMax === ThresholdMin) {
        totalItemCondition = TotalItemConditionType.Exactly;
      }

      if (!ThresholdMax) {
        totalItemCondition = TotalItemConditionType.Every;
      }

      if (ThresholdMax === 10000) {
        totalItemCondition = TotalItemConditionType.AtLeast;
      }

      return {
        maximumItems: ThresholdMax,
        minimumGrams: undefined,
        minimumItems: ThresholdMin,
        minimumSpend: undefined,
        totalItemCondition,
      };
    }

    // if there is no threshold set on the discount, all min/max items should be defaults
    return {
      maximumItems: undefined,
      minimumGrams: undefined,
      minimumItems: undefined,
      minimumSpend: undefined,
    };
  };

  const mapConstraintsToRequirements = (
    constraints: Omit<Constraint, 'Products'>[],
    reward: Omit<Reward, 'Products'>,
    discountRewardThresholdDetails: ThresholdDetails,
    discountRewardFilters: Filters
  ) => {
    const requirements: Record<number, Requirement> = {};

    if (constraints.length === 0) {
      requirements[1] = {
        condition: reward.ThresholdTypeId ?? 0,
        filters: { ...discountRewardFilters },
        maximumItems: discountRewardThresholdDetails.maximumItems ?? '',
        minimumGrams: discountRewardThresholdDetails.minimumGrams ?? '',
        minimumItems: discountRewardThresholdDetails.minimumItems ?? '',
        minimumSpend: discountRewardThresholdDetails.minimumSpend ?? '',
        requireSameProduct: reward.ItemGroupTypeId === ItemGroupType.BySKU,
        allowNonCannabisItems: reward.IncludeNonCannabis,
        totalItemCondition: discountRewardThresholdDetails.totalItemCondition ?? 0,
        id: null,
      };
      return requirements;
    }

    constraints.forEach((constraint, index) => {
      const requirementFilters = getRewardFilters(constraint.Restrictions);
      const { ThresholdMax, ThresholdMin, ThresholdTypeId } = constraint;
      const requirementThresholdDetails = getThresholdDetails({
        ThresholdMax,
        ThresholdMin,
        ThresholdTypeId,
      });
      requirements[index + 1] = {
        condition: constraint.ThresholdTypeId ?? 0,
        filters: { ...requirementFilters },
        maximumItems: requirementThresholdDetails.maximumItems ?? '',
        minimumGrams: requirementThresholdDetails.minimumGrams ?? '',
        minimumItems: requirementThresholdDetails.minimumItems ?? '',
        minimumSpend: requirementThresholdDetails.minimumSpend ?? '',
        requireSameProduct: constraint.DiscountItemGroupTypeId === ItemGroupType.BySKU,
        allowNonCannabisItems: constraint.IncludeNonCannabis,
        totalItemCondition: requirementThresholdDetails.totalItemCondition ?? 0,
        id: constraint.DiscountConstraintId || null,
      };
    });

    return requirements;
  };

  const setDirty = useCallback(() => setDirtyState(true), []);

  return {
    configureStepState,
    configureStepDispatch: useDispatchWithSideEffect(configureStepDispatchBase, setDirty),
    defaultStepToOpenOn,
    detailsStepState,
    detailsStepDispatch: useDispatchWithSideEffect(detailsStepDispatchBase, setDirty),
    dirty,
    discountId,
    isManualDiscount: configureStepState.applicationMethod === ApplicationMethodTypeId.Manually,
    loading,
    onlineDetailsStepState,
    onlineDetailsStepDispatch: useDispatchWithSideEffect(onlineDetailsStepDispatchBase, setDirty),
    requirementsStepState,
    requirementsStepDispatch: useDispatchWithSideEffect(requirementsStepDispatchBase, setDirty),
    rewardsStepState,
    rewardsStepDispatch: useDispatchWithSideEffect(rewardsStepDispatchBase, setDirty),
    reset,
    showEligibleProductsModal,
    setShowEligibleProductsModal,
    eligibleProductsModalRequirement,
    setEligibleProductsModalRequirement,
  };
}

export const DiscountWizardStateContext = createContext<ReturnType<typeof useDiscountWizardState>>({
  configureStepDispatch: () => null,
  configureStepState: { ...configureStepDefaultState },
  defaultStepToOpenOn: undefined,
  detailsStepState: { ...detailsStepDefaultState },
  detailsStepDispatch: () => null,
  dirty: false,
  discountId: 0,
  isManualDiscount: false,
  loading: false,
  onlineDetailsStepState: { ...onlineDetailsStepDefaultState },
  onlineDetailsStepDispatch: () => null,
  requirementsStepState: { ...requirementsStepDefaultState },
  requirementsStepDispatch: () => null,
  rewardsStepState: { ...rewardsStepDefaultState },
  rewardsStepDispatch: () => null,
  reset: () => null,
  showEligibleProductsModal: false,
  setShowEligibleProductsModal: () => null,
  eligibleProductsModalRequirement: 'reward',
  setEligibleProductsModalRequirement: () => null,
});

export function useDiscountWizardStateContext() {
  return useContext(DiscountWizardStateContext);
}
